// Standalone Nav + Footer for v13 handwerker variant.
// Mountable on interior pages via veaury — uses shared Link, no Inertia react import.
import React, { useState, useEffect } from 'react';
import { Link } from '../_shared.jsx';

const theme = {
  bg: '#1a1612', fg: '#f5efe1', muted: '#a99c84',
  border: 'rgba(245, 239, 225, 0.15)', accent: '#d4a44e', accentFg: '#1a1612',
  inputBg: '#231d17', radius: 4,
  font: '"Inter", system-ui, sans-serif',
  serif: '"Playfair Display", "Times New Roman", serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  bgAlt: '#211c17',
  bgDeep: '#0f0d0a',
  accentSoft: 'rgba(212, 164, 78, 0.18)',
  accentSofter: 'rgba(212, 164, 78, 0.08)',
  glass: 'rgba(255, 255, 255, 0.03)',
  glassStrong: 'rgba(255, 255, 255, 0.04)',
  creamMuted: '#bdb09a',
};

const monoGold = {
  fontFamily: theme.mono, color: theme.accent,
  letterSpacing: '0.14em', textTransform: 'uppercase',
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : true);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update); };
  }, []);
  return isMobile;
};

const HamburgerIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {open ? <><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></> :
            <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
  </svg>
);

const navLinks = [
  { label: 'Über uns',     href: '/über-uns' },
  { label: 'Wärmepumpe',   href: '/wärmepumpe' },
  { label: 'Photovoltaik', href: '/photovoltaik' },
  { label: 'Termin',       href: '/kontakt' },
];

export function V13HandwerkerNav({ brand = 'Waemo', brandLogo = null }) {
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const brandLabel = brand || 'Waemo';
  return (
    <div style={{
      padding: isMobile ? '12px 16px' : '14px 48px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      borderBottom: `1px solid ${theme.border}`,
      position: 'relative', background: theme.bg,
    }}>
      <Link href="/" style={{ display: 'flex', alignItems: 'baseline', gap: 8, color: 'inherit', textDecoration: 'none' }} aria-label={brandLabel + ' Startseite'}>
        {brandLogo
          ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
          : <>
              <div style={{ fontFamily: theme.serif, fontWeight: 800, fontSize: isMobile ? 18 : 22, letterSpacing: '-0.01em', color: theme.fg }}>{brand}</div>
              {!isMobile && (
                <div style={{ fontSize: 11, color: theme.accent, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 600 }}>Premium Wärmepumpen</div>
              )}
            </>
        }
      </Link>
      {!isMobile && (
        <div style={{ display: 'flex', gap: 26, fontSize: 13, color: theme.muted }}>
          {navLinks.map(l => <Link key={l.label} href={l.href} style={{ color: 'inherit', textDecoration: 'none' }}>{l.label}</Link>)}
        </div>
      )}
      {isMobile && (
        <button
          aria-label="Menü"
          onClick={() => setIsMenuOpen(o => !o)}
          style={{ background: 'transparent', border: 'none', color: theme.fg, cursor: 'pointer', padding: 4, display: 'flex' }}
        >
          <HamburgerIcon open={isMenuOpen} />
        </button>
      )}
      {isMobile && isMenuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: theme.bg, borderBottom: `1px solid ${theme.border}`,
          padding: '14px 16px 18px', display: 'flex', flexDirection: 'column', gap: 14,
          zIndex: 50,
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{ color: theme.fg, textDecoration: 'none', fontSize: 14 }}>{l.label}</Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function V13HandwerkerFooter({ brand = 'Waemo', brandLogo = null }) {
  const isMobile = useIsMobile();
  const brandLabel = brand || 'Waemo';
  return (
    <footer style={{
      background: theme.bg,
      borderTop: `1px solid rgba(212, 164, 78, 0.2)`,
      padding: isMobile ? '28px 16px 20px' : '48px 48px 28px',
    }}>
      <div style={{
        maxWidth: 1184, margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1.4fr 1fr',
        gap: isMobile ? 24 : 40,
        alignItems: 'flex-start',
      }}>
        {/* Brand */}
        <div>
          {brandLogo
            ? <img src={brandLogo} alt={brandLabel + ' Logo'} width="120" height="32" style={{ height: 32, width: 'auto', maxWidth: 160, display: 'block' }} />
            : <>
                <div style={{
                  fontFamily: theme.serif, fontWeight: 800, fontSize: isMobile ? 22 : 26,
                  letterSpacing: '-0.01em', color: theme.accent,
                }}>
                  {brand}
                </div>
                <div style={{
                  ...monoGold, fontSize: 10.5, marginTop: 6,
                  color: theme.accent,
                }}>
                  Premium Wärmepumpen
                </div>
              </>
          }
          <div style={{
            fontFamily: theme.serif, fontStyle: 'italic',
            fontSize: 13, color: theme.muted, marginTop: 14,
            lineHeight: 1.5, maxWidth: 240,
          }}>
            Vergleichsportal. Bundesweite Vermittlung.
          </div>
        </div>

        {/* Nav */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 10 : 28,
          justifyContent: isMobile ? 'flex-start' : 'center',
          flexWrap: 'wrap',
        }}>
          {navLinks.map(l => (
            <Link key={l.label} href={l.href} style={{
              fontFamily: theme.font, fontSize: 13, color: theme.fg,
              cursor: 'pointer', textDecoration: 'none',
            }}>
              {l.label}
            </Link>
          ))}
        </div>

        {/* Sitz (no legal entity here — legal details live in Impressum) */}
        <div style={{
          ...monoGold, fontSize: 10.5, color: theme.muted,
          lineHeight: 1.9, textAlign: isMobile ? 'left' : 'right',
        }}>
          <div>HANNOVER</div>
          <div>BUNDESWEITE VERMITTLUNG</div>
        </div>
      </div>

      <div style={{
        maxWidth: 1184, margin: isMobile ? '20px auto 0' : '32px auto 0',
        paddingTop: 18, borderTop: `1px solid ${theme.accentSoft}`,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 10 : 20, flexWrap: 'wrap',
      }}>
        <div style={{
          fontFamily: theme.mono, fontSize: 11,
          color: theme.muted, letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>
          {`© ${new Date().getFullYear()} ${brand}`}
        </div>
        <div style={{
          fontFamily: theme.mono, fontSize: 11,
          color: theme.muted, letterSpacing: '0.14em', textTransform: 'uppercase',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 6 : 22,
        }}>
          <Link href="/impressum" style={{ color: 'inherit', textDecoration: 'none' }}>Impressum</Link>
          <Link href="/datenschutz" style={{ color: 'inherit', textDecoration: 'none' }}>Datenschutz</Link>
          <Link href="/agb" style={{ color: 'inherit', textDecoration: 'none' }}>AGB</Link>
          <Link href="/widerruf" style={{ color: 'inherit', textDecoration: 'none' }}>Widerruf</Link>
        </div>
      </div>
    </footer>
  );
}
